/**
 * SEO Utilities for LC Performance
 * 
 * Functions for generating slugs, stripping HTML, calculating read time,
 * extracting keywords, and generating meta tags for blog posts.
 */

/**
 * Generate a URL-friendly slug from a title
 * @param {string} title - The title to convert to a slug
 * @returns {string} URL-friendly slug
 */
export function generateSlug(title) {
  if (!title) return '';
  
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}

/**
 * Strip HTML tags from content, preserving text
 * @param {string} html - HTML content to strip
 * @returns {string} Plain text content
 */
export function stripHtml(html) {
  if (!html) return '';
  
  // Create a temporary DOM element to parse HTML
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  
  // Get text content and clean up whitespace
  return tmp.textContent || tmp.innerText || ''
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim();
}

/**
 * Generate meta description from content
 * @param {string} content - Content to generate description from
 * @param {number} maxLength - Maximum length of description (default: 160)
 * @returns {string} Meta description
 */
export function generateMetaDescription(content, maxLength = 160) {
  if (!content) return '';
  
  const text = stripHtml(content);
  
  if (text.length <= maxLength) {
    return text;
  }
  
  // Truncate at word boundary
  const truncated = text.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  if (lastSpace > 0) {
    return truncated.substring(0, lastSpace) + '...';
  }
  
  return truncated + '...';
}

/**
 * Extract keywords from content
 * @param {string} content - Content to extract keywords from
 * @param {number} count - Number of keywords to extract (default: 10)
 * @returns {string[]} Array of keywords
 */
export function extractKeywords(content, count = 10) {
  if (!content) return [];
  
  const text = stripHtml(content).toLowerCase();
  
  // Common stop words to exclude
  const stopWords = new Set([
    'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
    'of', 'with', 'by', 'from', 'as', 'is', 'was', 'are', 'been', 'be',
    'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'should',
    'could', 'may', 'might', 'must', 'can', 'this', 'that', 'these', 'those',
    'i', 'you', 'he', 'she', 'it', 'we', 'they', 'what', 'which', 'who',
    'when', 'where', 'why', 'how', 'all', 'each', 'every', 'both', 'few',
    'more', 'most', 'other', 'some', 'such', 'no', 'nor', 'not', 'only',
    'own', 'same', 'so', 'than', 'too', 'very', 'just', 'now'
  ]);
  
  // Extract words (alphanumeric, at least 3 characters)
  const words = text.match(/\b[a-z]{3,}\b/g) || [];
  
  // Count word frequency
  const wordCount = {};
  words.forEach(word => {
    if (!stopWords.has(word)) {
      wordCount[word] = (wordCount[word] || 0) + 1;
    }
  });
  
  // Sort by frequency and get top keywords
  const sorted = Object.entries(wordCount)
    .sort((a, b) => b[1] - a[1])
    .slice(0, count)
    .map(([word]) => word);
  
  return sorted;
}

/**
 * Calculate estimated reading time in minutes
 * @param {string} content - Content to calculate reading time for
 * @param {number} wordsPerMinute - Average reading speed (default: 200)
 * @returns {number} Reading time in minutes (minimum 1)
 */
export function calculateReadTime(content, wordsPerMinute = 200) {
  if (!content) return 1;
  
  const text = stripHtml(content);
  const wordCount = text.split(/\s+/).filter(word => word.length > 0).length;
  const minutes = Math.ceil(wordCount / wordsPerMinute);
  
  return Math.max(1, minutes); // Minimum 1 minute
}

/**
 * Generate meta tags object for a blog post
 * @param {Object} blog - Blog post object
 * @param {string} blog.title - Blog title
 * @param {string} blog.meta_title - Meta title (optional)
 * @param {string} blog.meta_description - Meta description (optional)
 * @param {string[]} blog.meta_keywords - Meta keywords array (optional)
 * @param {string} blog.excerpt - Blog excerpt (optional)
 * @param {string} blog.content - Blog content (optional)
 * @param {string} blog.header_image_url - Header image URL (optional)
 * @param {string} blog.slug - Blog slug
 * @param {string} siteUrl - Base URL of the site (optional)
 * @returns {Object} Meta tags object
 */
export function generateMetaTags(blog, siteUrl = '') {
  const title = blog.meta_title || blog.title || '';
  const description = blog.meta_description || blog.excerpt || 
    (blog.content ? generateMetaDescription(blog.content) : '');
  const keywords = blog.meta_keywords || 
    (blog.content ? extractKeywords(blog.content) : []);
  const image = blog.header_image_url || '';
  const url = siteUrl ? `${siteUrl}/blog/${blog.slug}` : `/blog/${blog.slug}`;
  
  return {
    title,
    description,
    keywords: Array.isArray(keywords) ? keywords.join(', ') : keywords,
    image,
    url,
    // Open Graph tags
    ogTitle: title,
    ogDescription: description,
    ogImage: image,
    ogUrl: url,
    ogType: 'article',
    // Twitter Card tags
    twitterCard: 'summary_large_image',
    twitterTitle: title,
    twitterDescription: description,
    twitterImage: image,
  };
}

/**
 * Generate a unique slug by appending a number if slug already exists
 * @param {string} baseSlug - Base slug to make unique
 * @param {Function} checkExists - Async function to check if slug exists (slug) => Promise<boolean>
 * @returns {Promise<string>} Unique slug
 */
export async function generateUniqueSlug(baseSlug, checkExists) {
  let slug = baseSlug;
  let counter = 1;
  
  while (await checkExists(slug)) {
    slug = `${baseSlug}-${counter}`;
    counter++;
  }
  
  return slug;
}
